import { DELETION_FLAG, DateSchema, type Fall, TIME_FRAME } from "@packages/core";
import { Link, createFileRoute, useRouter } from "@tanstack/react-router";
import { groupBy } from "es-toolkit";
import { useMemo } from "react";
import { LuArrowLeft, LuArrowRight, LuChevronLeft, LuPencil } from "react-icons/lu";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Temporal } from "temporal-polyfill";
import * as v from "valibot";

import { Breadcrumbs, ButtonStyle, PaperStyle, TypographyStyle } from "#components";
import { useListedFalls, useResident, useTranslate } from "#services";
import { css } from "#styled-system/css";

export const Route = createFileRoute(
  "/_authenticated/facilities/$facilityKey/residents/$userKey/falls/",
)({
  component: Component,
  validateSearch: v.partial(
    v.object({
      date: v.fallback(DateSchema(), function () {
        return Temporal.Now.plainDateISO("Asia/Tokyo");
      }),
      numberOfDays: v.fallback(v.pipe(v.number(), v.integer(), v.minValue(1)), 1),
    }),
  ),
});

function Component() {
  const { facilityKey, userKey } = Route.useParams();
  const { date: _date, numberOfDays = 1 } = Route.useSearch();

  const resident = useResident(userKey);
  const router = useRouter();
  const translate = useTranslate();

  const date = useMemo(
    function () {
      return _date ?? Temporal.Now.plainDateISO("Asia/Tokyo");
    },
    [_date],
  );

  const period = useMemo(
    function () {
      return [
        date.subtract({ days: numberOfDays }).toPlainDateTime({ hour: TIME_FRAME.DAYTIME[0] }),
        date.add({ days: 1 }).toPlainDateTime({ hour: TIME_FRAME.DAYTIME[0] }),
      ] as const;
    },
    [date, numberOfDays],
  );

  const { pages } = useListedFalls({
    fellAt: ["-", ...period],
    deleted: DELETION_FLAG.DEFAULT,
    userKey,
  });

  const falls = useMemo(
    function () {
      return pages.flatMap(function (page) {
        return page.falls;
      });
    },
    [pages],
  );

  const data = useMemo(
    function () {
      return Object.entries(
        Object.assign(
          Object.fromEntries(
            Array.from({ length: period[0].until(period[1]).days }, function (_, i) {
              return [period[0].add({ days: i }).toString(), []];
            }),
          ),
          groupBy(falls, function (fall) {
            return fall.fellAt.toPlainDate().toString();
          }),
        ),
      ).map(function ([x, falls]) {
        return { date: Temporal.PlainDate.from(x), falls, x, y: falls.length };
      });
    },
    [falls, period],
  );

  return (
    <main
      className={css({
        paddingLeft: { smDown: "0 !important" },
        paddingRight: { smDown: "0 !important" },
      })}
    >
      <div
        className={css({
          alignItems: "center",
          display: "flex",
          gap: "16px",
          justifyContent: "space-between",
        })}
      >
        <Link
          className={css({ alignItems: "center", display: "flex", gap: "4px", hideFrom: "sm" })}
          onClick={function (event) {
            if (router.history.canGoBack()) {
              event.preventDefault();
              event.stopPropagation();
              router.history.back();
            }
          }}
          params={{ facilityKey, userKey }}
          to="/facilities/$facilityKey/residents/$userKey"
        >
          <LuChevronLeft className={css({ height: "32px", width: "auto" })} />
          <p
            className={TypographyStyle({
              color: "primary",
              fontSize: "16px",
              fontWeight: 500,
              lineHeight: 1,
            })}
          >
            {translate("%HonorificTitle.0%", {
              $: resident ?? { name: translate("%Resident.0%") },
            })}
          </p>
        </Link>

        <Breadcrumbs
          className={css(
            TypographyStyle.raw({ fontSize: "14px", fontWeight: 500, lineHeight: 1 }),
            { display: "flex", hideBelow: "sm" },
          )}
        >
          <Link
            className={css({ color: "#71717A" })}
            params={{ facilityKey }}
            to="/facilities/$facilityKey"
          >
            {translate("%ResidentList.0%")}
          </Link>
          <Link
            className={css({ color: "#71717A" })}
            params={{ facilityKey, userKey }}
            to="/facilities/$facilityKey/residents/$userKey"
          >
            {translate("%HonorificTitle.0%", {
              $: resident ?? { name: translate("%Resident.0%") },
            })}
          </Link>
          <p>{translate("%Fall.0%")}</p>
        </Breadcrumbs>

        <Link
          className={css({ alignItems: "center", display: "flex", gap: "8px", padding: "8px" })}
          params={{ facilityKey, userKey }}
          search={{ yearMonth: date.toPlainYearMonth() }}
          to="/facilities/$facilityKey/residents/$userKey/falls/registration"
        >
          <LuPencil />
          <p className={TypographyStyle({ fontSize: "14px", fontWeight: 500, lineHeight: 1 })}>
            {translate("%Add.1%")}
          </p>
        </Link>
      </div>

      <div
        className={css({
          backgroundColor: "white",
          borderRadius: { sm: "12px" },
          marginTop: "16px",
          padding: "16px",
        })}
      >
        <h3
          className={css(TypographyStyle.raw({ lineHeight: 1 }), {
            fontSize: { base: "16px", sm: "20px" },
            fontWeight: { base: 500, sm: 700 },
            textAlign: { base: "center", sm: "left" },
          })}
        >
          {translate("%Fall.0%")}
        </h3>

        <div
          className={css({
            backgroundColor: "muted-background",
            borderRadius: "6px",
            display: "flex",
            marginTop: "12px",
            padding: "4px",
            width: "100%",
          })}
        >
          {[7, 30].map(function (nds, index) {
            return (
              <Link
                className={css(
                  TypographyStyle.raw({
                    fontSize: "14px",
                    fontWeight: 500,
                    lineHeight: 1,
                    textAlign: "center",
                  }),
                  {
                    borderRadius: "2px",
                    display: "block",
                    flexGrow: 1,
                    paddingX: "12px",
                    paddingY: "6px",
                    _selected: { backgroundColor: "background" },
                  },
                )}
                data-selected={numberOfDays === nds || undefined}
                key={index}
                params={{ facilityKey, userKey }}
                replace
                search={{ date, numberOfDays: nds }}
                to="/facilities/$facilityKey/residents/$userKey/falls"
              >
                {nds === 1
                  ? translate("%Day.0%")
                  : nds === 7
                    ? translate("%Week.0%")
                    : nds === 30
                      ? translate("%Month.0%")
                      : translate("%NumberOfDays.0%", { $: { numberOfDays: nds } })}
              </Link>
            );
          })}
        </div>

        <p
          className={css(TypographyStyle.raw({ color: "primary", fontSize: "14px" }), {
            marginTop: "12px",
            opacity: 0.6,
          })}
        >
          {translate("%Date.1%", { $: period[0] })}〜
          {translate("%Date.1%", { $: period[1].subtract({ days: 1 }) })}
        </p>

        <div className={css({ marginTop: "12px" })}>
          <p
            className={TypographyStyle({
              color: "primary/60",
              fontSize: "14px",
              fontWeight: 500,
              lineHeight: 1,
            })}
          >
            {translate("%NumberOfFalls.0%")}
          </p>
          <div
            className={css({
              alignItems: "baseline",
              display: "flex",
              gap: "2px",
              marginTop: "6px",
            })}
          >
            <span
              className={TypographyStyle({
                color: "primary",
                fontSize: "36px",
                fontWeight: 700,
                lineHeight: 1,
              })}
            >
              {falls.length}
            </span>
            <span
              className={TypographyStyle({
                color: "primary/60",
                fontSize: "18px",
                fontWeight: 700,
                lineHeight: 1,
              })}
            >
              {translate("%NumberOfFalls.1%")}
            </span>
          </div>
        </div>

        <div
          className={css({
            alignItems: "center",
            display: "flex",
            gap: "16px",
            justifyContent: "center",
          })}
        >
          <Link
            className={css(
              ButtonStyle.raw({
                appearance: "outlined",
                borderRadius: "100%",
                color: "outlined",
                icon: true,
                size: "32px",
              }),
              { hideBelow: "sm" },
            )}
            search={{ date: date.subtract({ days: numberOfDays }), numberOfDays }}
            to="."
          >
            <LuArrowLeft className={css({ height: "16px", width: "auto" })} />
          </Link>

          <ResponsiveContainer height={440} width="100%">
            <BarChart data={data}>
              <XAxis
                axisLine={false}
                dataKey="x"
                interval={numberOfDays <= 7 ? 0 : 5}
                tick={{ fill: "#18181B4D", fontSize: "12px" }}
                tickFormatter={function (x) {
                  return numberOfDays <= 7
                    ? translate("%DayOfWeek." + Temporal.PlainDate.from(x).dayOfWeek + ".0%")
                    : translate("%Date.3%", { $: Temporal.PlainDate.from(x) });
                }}
                tickLine={false}
              />
              <YAxis
                allowDecimals
                axisLine={false}
                dataKey="y"
                padding={{ top: 16, bottom: 16 }}
                tick={{ fill: "#18181B4D", fontSize: "12px" }}
                tickLine={false}
                type="number"
                width={16}
              />
              <Tooltip
                content={function ({ payload }) {
                  const date: Temporal.PlainDate | undefined = payload?.at(0)?.payload.date;
                  const falls: ReadonlyArray<Fall> | undefined = payload.at(0)?.payload.falls;
                  if (!date || !falls) {
                    return null;
                  }
                  return (
                    <div
                      className={css(PaperStyle.raw({ borderRadius: "12px" }), {
                        backgroundColor: "#FFFFFFCC",
                        padding: "16px",
                      })}
                    >
                      <p className={TypographyStyle({ color: "primary/60", fontSize: "12px" })}>
                        {translate("%Date.1%", { $: date })}
                      </p>
                      <p>
                        <span className={TypographyStyle({ color: "primary", fontSize: "12px" })}>
                          {translate("%NumberOfFalls.0%")}
                        </span>
                        &nbsp;
                        <span
                          className={TypographyStyle({
                            color: "primary",
                            fontSize: "14px",
                            fontWeight: 700,
                          })}
                        >
                          {falls.length}
                        </span>
                        &nbsp;
                        <span className={TypographyStyle({ color: "primary", fontSize: "12px" })}>
                          {translate("%NumberOfFalls.1%")}
                        </span>
                      </p>
                    </div>
                  );
                }}
              />
              <CartesianGrid stroke="#E4E4E7" syncWithTicks vertical={false} />
              <Bar dataKey="y" fill="#DC2626" />
            </BarChart>
          </ResponsiveContainer>

          <Link
            className={css(
              ButtonStyle.raw({
                appearance: "outlined",
                borderRadius: "100%",
                color: "outlined",
                icon: true,
                size: "32px",
              }),
              { hideBelow: "sm" },
            )}
            search={{ date: date.add({ days: numberOfDays }), numberOfDays }}
            to="."
          >
            <LuArrowRight className={css({ height: "16px", width: "auto" })} />
          </Link>
        </div>
      </div>
    </main>
  );
}
